import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import type { BrageSvenHendelse } from '@/state/GameState';
import { KATEGORI_NAVN } from '@/systems/brage';
import { animateModalGlide } from '@/ui/modalGlide';
import { sfx } from '@/systems/sfx';
import { autoSpeed } from '@/systems/autoSpeed';

const SHEET_WIDTH = 920;
const SHEET_HEIGHT = 920;

export class SvenBrageVinnerModal {
  private layer?: Phaser.GameObjects.Container;

  constructor(private scene: Phaser.Scene) {}

  get isOpen(): boolean {
    return !!this.layer;
  }

  open(hendelse: BrageSvenHendelse): void {
    this.close();
    autoSpeed.paus();
    sfx.play('fanfare');
    this.layer = this.scene.add.container(0, 0).setDepth(220);
    animateModalGlide(this.scene, this.layer, { spillLyd: false });

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.85)
      .setOrigin(0, 0)
      .setInteractive();
    backdrop.on('pointerup', () => this.close());
    this.layer.add(backdrop);

    const sheetX = (GAME_WIDTH - SHEET_WIDTH) / 2;
    const sheetY = (GAME_HEIGHT - SHEET_HEIGHT) / 2;

    const sheet = this.scene.add
      .rectangle(sheetX, sheetY, SHEET_WIDTH, SHEET_HEIGHT, COLORS.wine, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(6, 0xb8860b, 1);
    this.layer.add(sheet);

    const innerPad = 24;
    const inner = this.scene.add
      .rectangle(
        sheetX + innerPad,
        sheetY + innerPad,
        SHEET_WIDTH - innerPad * 2,
        SHEET_HEIGHT - innerPad * 2,
        0x4a1620,
        1,
      )
      .setOrigin(0, 0)
      .setStrokeStyle(2, 0xb8860b, 0.7);
    this.layer.add(inner);

    const krone = this.scene.add
      .text(sheetX + SHEET_WIDTH / 2, sheetY + 80, '🏆', {
        fontFamily: FONTS.display,
        fontSize: fontStr(96),
        color: '#b8860b',
      })
      .setOrigin(0.5, 0);
    this.layer.add(krone);

    const tittel = this.scene.add
      .text(sheetX + SHEET_WIDTH / 2, sheetY + 230, gameState.spillerFulltNavn, {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#f4ead5',
        align: 'center',
        wordWrap: { width: SHEET_WIDTH - 96 },
      })
      .setOrigin(0.5, 0);
    this.layer.add(tittel);

    const undertittel = this.scene.add
      .text(sheetX + SHEET_WIDTH / 2, sheetY + 340, 'vinner Brage', {
        fontFamily: FONTS.display,
        fontSize: fontStr(48),
        color: '#b8860b',
        align: 'center',
      })
      .setOrigin(0.5, 0);
    this.layer.add(undertittel);

    const kategori = this.scene.add
      .text(
        sheetX + SHEET_WIDTH / 2,
        sheetY + 430,
        `i kategorien ${KATEGORI_NAVN[hendelse.kategori]} ${hendelse.aar}`,
        {
          fontFamily: FONTS.body,
          fontSize: fontStr(22),
          color: '#f4d58d',
          fontStyle: 'italic',
          align: 'center',
          wordWrap: { width: SHEET_WIDTH - 96 },
        },
      )
      .setOrigin(0.5, 0);
    this.layer.add(kategori);

    const bokTittel = this.scene.add
      .text(sheetX + SHEET_WIDTH / 2, sheetY + 510, `«${hendelse.tittel}»`, {
        fontFamily: FONTS.display,
        fontSize: fontStr(36),
        color: '#f4ead5',
        fontStyle: 'italic',
        align: 'center',
        wordWrap: { width: SHEET_WIDTH - 96 },
      })
      .setOrigin(0.5, 0);
    this.layer.add(bokTittel);

    if (hendelse.publiseringsvei) {
      const veiTekst = veiOmtale(hendelse.publiseringsvei);
      const note = this.scene.add
        .text(sheetX + SHEET_WIDTH / 2, sheetY + 620, veiTekst, {
          fontFamily: FONTS.body,
          fontSize: fontStr(16),
          color: '#d9a14b',
          fontStyle: 'italic',
          align: 'center',
          wordWrap: { width: SHEET_WIDTH - 96 },
        })
        .setOrigin(0.5, 0);
      this.layer.add(note);
    }

    const knappW = 280;
    const knappH = 72;
    const knappX = sheetX + (SHEET_WIDTH - knappW) / 2;
    const knappY = sheetY + SHEET_HEIGHT - 110;
    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, 0xb8860b, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, COLORS.wine, 0.7);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, 'Ta imot prisen', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#1a1a1a',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);
    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setAlpha(0.9));
    knappBg.on('pointerout', () => knappBg.setAlpha(1));
    knappBg.on('pointerup', () => this.close());
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
    autoSpeed.fortsett();
  }
}

function veiOmtale(
  vei:
    | 'eget-forlag'
    | 'kapelle'
    | 'asketank'
    | 'gullsand'
    | 'calidris',
): string {
  if (vei === 'eget-forlag') {
    return 'Til tross for at du publiserte i ditt eget forlag — juryen overbeviste seg om manuset.';
  }
  if (vei === 'calidris') {
    return 'Selv Calidris-stempelet kunne ikke skjule kvaliteten.';
  }
  if (vei === 'asketank') return 'Asketank tar en velfortjent runde i rampelyset.';
  if (vei === 'kapelle') return 'Kapelle Damm har stadig en vinner i stallen.';
  return 'Gullsand-bok i toppen.';
}
